import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail, Smartphone, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { solicitarOTP, verificarOTP } from '@/services/api';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { config } = useRegion();
  
  // DDI automático por região (BR=55, PT=351). Persiste último DDI usado.
  const ddiPadrao = config.code === 'PT' ? '351' : '55';
  const lastDDI = (() => {
    try { return localStorage.getItem('rota017_last_ddi') || ddiPadrao; } catch { return ddiPadrao; }
  })();
  const lastPhone = (() => {
    try { return localStorage.getItem('rota017_last_phone') || ''; } catch { return ''; }
  })();
  
  const [mode, setMode] = useState('phone'); // 'phone' | 'email'
  const [step, setStep] = useState('input');
  const [ddi, setDdi] = useState(lastDDI);
  const [telefone, setTelefone] = useState(lastPhone);
  const [codigo, setCodigo] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [rememberDevice, setRememberDevice] = useState(true);
  
  // Telefone completo (DDI + número) para enviar à API
  const telefoneCompleto = (ddi + telefone).replace(/\D/g, '');
  
  const redirecionarPorPerfil = (perfil) => {
    if (perfil === 'super_admin' || perfil === 'master_admin') navigate('/dashboard/super-admin');
    else if (perfil === 'cliente') navigate('/dashboard/cliente');
    else if (perfil === 'entregador') navigate('/app/entregador');
    else if (perfil === 'funcionario') navigate('/dashboard/funcionario');
    else navigate('/dashboard/cliente');
  };
  
  const handleSolicitarOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Salva DDI + número para próxima visita
      try {
        localStorage.setItem('rota017_last_ddi', ddi);
        localStorage.setItem('rota017_last_phone', telefone);
      } catch { /* noop */ }
      
      const response = await solicitarOTP(telefoneCompleto);
      if (response.data.success) {
        toast.success('Código enviado!');
        if (response.data.dev_code) {
          setDevCode(response.data.dev_code);
        }
        setStep('otp');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar código');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerificarOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await verificarOTP(telefoneCompleto, codigo, rememberDevice);
      if (response.data.success && response.data.verified) {
        if (response.data.new_user) {
          toast.info('Novo usuário! Complete o cadastro.');
          navigate('/cadastro', { state: { telefone: telefoneCompleto } });
        } else {
          if (response.data.token) {
            localStorage.setItem('rota017_token', response.data.token);
            if (rememberDevice) {
              localStorage.setItem('rota017_remember', 'true');
            }
          }
          login(response.data.usuario, response.data.empresa);
          toast.success('Login realizado com sucesso!');
          redirecionarPorPerfil(response.data.usuario.perfil);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoginEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login-email', { email, password, remember: rememberDevice });
      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('rota017_token', response.data.token);
        }
        login(response.data.usuario, response.data.empresa);
        toast.success('Bem-vindo, ' + (response.data.usuario.nome || 'Admin') + '!');
        redirecionarPorPerfil(response.data.usuario.perfil);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };
  
  const trocarModo = (novoModo) => {
    setMode(novoModo);
    setStep('input');
    setCodigo('');
    setDevCode(null);
  };
  
  return (
    <div className="min-h-screen flex">
      {/* Hero Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1543499459-d1460946bdc6?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      >
        <div className="absolute inset-0 bg-orange-600/30"></div>
      </div>
      
      {/* Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-orange-50">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 mb-6 transition-colors"
            data-testid="back-to-landing"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o início
          </Link>
          
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo-rota017.png" alt="Rota 017" className="w-12 h-12 rounded-xl object-cover" />
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Rota 017
              </h1>
              <p className="text-sm text-slate-600">Seu pedido, nossa rota!</p>
            </div>
          </div>
          
          {/* Toggle de modo */}
          <div className="bg-white rounded-xl p-1 mb-6 grid grid-cols-2 gap-1 border border-slate-200" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => trocarModo('phone')}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                mode === 'phone' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="mode-phone-btn"
            >
              <Smartphone className="w-4 h-4" />
              Celular
            </button>
            <button
              type="button"
              onClick={() => trocarModo('email')}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                mode === 'email' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="mode-email-btn"
            >
              <Mail className="w-4 h-4" />
              E-mail
            </button>
          </div>
          
          {/* Form Email */}
          {mode === 'email' && (
            <form onSubmit={handleLoginEmail} className="space-y-5" data-testid="form-email-login">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Acesso Administrativo
                </h2>
                <p className="text-slate-600 text-sm">Entre com seu e-mail e senha</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">E-mail</label>
                <Input
                  data-testid="email-input"
                  type="email"
                  placeholder="suporte@rota017.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 focus:border-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Senha</label>
                <div className="relative">
                  <Input
                    data-testid="password-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-10 focus:border-orange-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 accent-orange-600"
                  data-testid="remember-device-email-checkbox"
                />
                <span className="text-xs text-slate-700">Lembrar deste dispositivo por 60 dias</span>
              </label>
              
              <Button
                data-testid="submit-email-login"
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</>
                ) : (
                  <><KeyRound className="w-4 h-4 mr-2" /> Entrar</>
                )}
              </Button>
            </form>
          )}
          
          {/* Form Phone */}
          {mode === 'phone' && step === 'input' && (
            <form onSubmit={handleSolicitarOTP} className="space-y-5" data-testid="form-phone-login">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Entrar na Plataforma
                </h2>
                <p className="text-slate-600 text-sm">Digite seu WhatsApp para receber o código de acesso</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Número WhatsApp</label>
                <div className="flex gap-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium pointer-events-none">+</span>
                    <Input
                      data-testid="ddi-input"
                      type="tel"
                      value={ddi}
                      onChange={(e) => setDdi(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      className="h-12 w-20 pl-6 text-center font-medium"
                      placeholder="55"
                      title="Código do país (DDI)"
                    />
                  </div>
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    placeholder={config.code === 'PT' ? '912345678' : '11999999999'}
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value.replace(/\D/g, ''))}
                    required
                    className="h-12 focus:border-orange-500 flex-1"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {config.code === 'PT'
                    ? 'Ex: +351 912345678 (sem espaços ou traços)'
                    : 'Ex: +55 11999999999 (sem espaços ou traços)'}
                </p>
              </div>
              
              <Button
                data-testid="request-otp-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                ) : 'Receber Código'}
              </Button>
            </form>
          )}
          
          {mode === 'phone' && step === 'otp' && (
            <form onSubmit={handleVerificarOTP} className="space-y-5" data-testid="form-otp">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Verificar Código
                </h2>
                <p className="text-slate-600 text-sm">
                  Digite o código de 6 dígitos enviado para <br />
                  <span className="font-mono font-semibold">+{telefoneCompleto}</span>
                </p>
              </div>
              
              {devCode && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 text-center" data-testid="dev-otp-box">
                  <p className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">
                    Código de acesso (modo demo)
                  </p>
                  <p className="text-4xl font-mono font-bold text-orange-700 tracking-widest" data-testid="dev-otp-code">
                    {devCode}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setCodigo(devCode); }}
                    className="mt-2 text-xs text-orange-600 hover:underline"
                    data-testid="auto-fill-otp-btn"
                  >
                    Preencher automaticamente
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Código OTP</label>
                <Input
                  data-testid="otp-input"
                  type="text"
                  placeholder="000000"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                  required
                  maxLength={6}
                  className="h-12 text-center text-2xl font-mono tracking-widest focus:border-orange-500"
                />
              </div>
              
              {/* Lembrar deste dispositivo (sessão de 60 dias) */}
              <label className="flex items-center gap-3 cursor-pointer select-none bg-orange-50/60 rounded-xl p-3 border border-orange-200">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-5 h-5 accent-orange-600"
                  data-testid="remember-device-checkbox"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">Lembrar deste dispositivo</p>
                  <p className="text-xs text-slate-500">
                    Não pedir código novamente neste celular por 60 dias
                  </p>
                </div>
              </label>
              
              <div className="space-y-3">
                <Button
                  data-testid="verify-otp-button"
                  type="submit"
                  disabled={loading || codigo.length !== 6}
                  className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</>
                  ) : 'Entrar'}
                </Button>
                
                <Button
                  data-testid="back-button"
                  type="button"
                  variant="outline"
                  onClick={() => { setStep('input'); setCodigo(''); setDevCode(null); }}
                  className="w-full"
                >
                  Voltar
                </Button>
              </div>
            </form>
          )}
          
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              Ao entrar, você concorda com os <Link to="/privacidade" className="text-orange-600 hover:underline">Termos de Uso e Política de Privacidade</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
